/*
 * Offers drawer.
 *
 * Owns the panel and the accordion inside it: opening, closing, the escape
 * key, focus handling and aria-expanded.
 *
 * It also moves the mobile button into the menu drawer, under the search field.
 * That drawer is rendered by the header section, so Liquid cannot put the
 * button there. The button is display:none until it lands in the drawer, which
 * keeps it out of the layout while this script is still pending, and the move
 * itself happens while the drawer is off screen. It is redone on
 * shopify:section:load because re-rendering the header throws the moved node
 * away.
 */
if (!customElements.get('offers-drawer')) {
  class OffersDrawer extends HTMLElement {
    constructor() {
      super();
      this.bodyClass = 'offers-drawer-is-open';
      /* Bound once, so re-connecting the element cannot stack listeners. */
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.placeMobileTrigger = this.placeMobileTrigger.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-offers-panel]');
      this.mobile = this.querySelector('[data-offers-mobile]');
      this.list = this.querySelector('[data-offers-list]');
      this.singleOpen = this.list?.dataset.singleOpen === 'true';
      /*
       * The mobile button leaves this element, so its toggle cannot rely on the
       * delegated listener below. Every toggle gets a listener of its own.
       */
      this.toggles = this.querySelectorAll('[data-offers-toggle]');
      this.toggles.forEach((toggle) => {
        toggle.removeEventListener('click', this.onToggleClick);
        toggle.addEventListener('click', this.onToggleClick);
      });

      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:section:load', this.placeMobileTrigger);

      this.placeMobileTrigger();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:section:load', this.placeMobileTrigger);
      this.mobile?.remove();
      document.body.classList.remove(this.bodyClass);
    }

    placeMobileTrigger() {
      if (!this.mobile) {
        return;
      }

      const drawer = document.querySelector('.menu-drawer__inner');
      if (!drawer) {
        /* No menu to move into: show it where it stands rather than losing it. */
        this.mobile.classList.add('offers__mobile--standalone');
        return;
      }

      if (drawer.contains(this.mobile)) {
        return;
      }

      this.mobile.classList.remove('offers__mobile--standalone');

      /* Under the search field when there is one, otherwise at the top. */
      const search = drawer.querySelector('.menu-drawer__header');
      if (search) {
        search.insertAdjacentElement('afterend', this.mobile);
      } else {
        drawer.prepend(this.mobile);
      }
    }

    onToggleClick(event) {
      event.preventDefault();
      this.toggle();
    }

    onClick(event) {
      if (event.target.closest('[data-offers-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const trigger = event.target.closest('[data-offers-trigger]');
      if (trigger) {
        event.preventDefault();
        this.toggleOffer(trigger);
      }
    }

    toggleOffer(trigger) {
      const item = trigger.closest('[data-offers-item]');
      if (!item) {
        return;
      }

      const open = !item.classList.contains('offers__item--open');
      if (open && this.singleOpen) {
        this.items().forEach((other) => {
          if (other !== item) {
            other.classList.remove('offers__item--open');
          }
        });
      }

      item.classList.toggle('offers__item--open', open);
      this.syncOffers();
    }

    items() {
      return Array.from(this.querySelectorAll('[data-offers-item]'));
    }

    onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') {
        return;
      }
      if (document.body.classList.contains(this.bodyClass)) {
        this.close();
      }
    }

    syncOffers() {
      this.items().forEach((item) => {
        const trigger = item.querySelector('[data-offers-trigger]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', item.classList.contains('offers__item--open'));
        }
      });
    }

    toggle() {
      if (document.body.classList.contains(this.bodyClass)) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.lastFocus = document.activeElement;
      this.closeOtherPanels();
      document.body.classList.add(this.bodyClass);
      this.setExpanded(true);

      if (this.panel) {
        this.panel.focus({ preventScroll: true });
      }
    }

    close() {
      document.body.classList.remove(this.bodyClass);
      this.setExpanded(false);

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
        this.lastFocus = null;
      }
    }

    /*
     * Never stack two off canvas panels. The mobile menu is closed through its
     * own element, because that is what releases the focus trap it installed;
     * dropping the body class alone would leave the keyboard stuck in the
     * header.
     */
    closeOtherPanels() {
      const nav = document.querySelector('toggle-mobile-navigation');
      if (nav && customElements.get('toggle-mobile-navigation')) {
        nav.remove();
      }

      document.body.classList.remove(
        'cart-drawer-is-open',
        'menu-drawer-is-open',
        'header-search-is-open',
        'filter-is-open'
      );

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }
    }

    setExpanded(value) {
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', value));
    }
  }

  window.OffersDrawer = OffersDrawer;
  customElements.define('offers-drawer', OffersDrawer);
}
